import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Typography,
  keyframes,
} from '@mui/material';
import {
  EmailOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
  Language as LanguageIcon,
} from '@mui/icons-material';
import authService from '../../services/auth.service';
import branding from '../../config/branding';

const PRIMARY = branding.colors.primary;
const NAVY = '#0f1724';
const WARM_WHITE = '#faf8f5';

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const softZoom = keyframes`
  from {
    transform: scale(1);
  }
  to {
    transform: scale(1.06);
  }
`;

const accentReveal = keyframes`
  from {
    width: 0;
    opacity: 0;
  }
  to {
    width: 48px;
    opacity: 1;
  }
`;

const Login: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isArabic = i18n.language === 'ar';

  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const fontFamily = isArabic
    ? '"Cairo", "Plus Jakarta Sans", sans-serif'
    : '"Plus Jakarta Sans", "Cairo", sans-serif';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'rememberMe' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authService.login({
        identifier: formData.identifier,
        password: formData.password,
      });

      if (formData.rememberMe) {
        localStorage.setItem('rememberMe', 'true');
      } else {
        localStorage.removeItem('rememberMe');
      }

      navigate('/dashboard');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message || t('auth.loginError');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const toggleLanguage = () => {
    i18n.changeLanguage(isArabic ? 'en' : 'ar');
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      bgcolor: 'rgba(255,255,255,0.72)',
      transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        bgcolor: '#fff',
      },
      '&.Mui-focused': {
        bgcolor: '#fff',
        boxShadow: `0 0 0 3px ${PRIMARY}22`,
      },
      '& fieldset': {
        borderColor: 'rgba(15, 23, 36, 0.12)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(15, 23, 36, 0.28)',
      },
      '&.Mui-focused fieldset': {
        borderColor: PRIMARY,
        borderWidth: 1.5,
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: PRIMARY,
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        display: 'flex',
        overflow: 'hidden',
        fontFamily,
        color: NAVY,
      }}
    >
      {/* Full-bleed hero photography */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/login.jpeg"
          alt=""
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: { xs: '62% center', md: 'center' },
            display: 'block',
            animation: `${softZoom} 28s ease-out forwards`,
          }}
        />
        {/* Atmosphere: warm dusk overlay + readable form side */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: {
              xs: `linear-gradient(165deg, rgba(15,23,36,0.78) 0%, rgba(15,23,36,0.55) 42%, rgba(245,106,44,0.18) 100%)`,
              md: `linear-gradient(105deg, rgba(15,23,36,0.88) 0%, rgba(15,23,36,0.72) 38%, rgba(15,23,36,0.28) 62%, rgba(245,106,44,0.12) 100%)`,
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `radial-gradient(ellipse 70% 50% at 20% 80%, ${PRIMARY}18, transparent 55%)`,
            pointerEvents: 'none',
          }}
        />
      </Box>

      {/* Language toggle */}
      <Button
        onClick={toggleLanguage}
        startIcon={<LanguageIcon sx={{ fontSize: 18 }} />}
        sx={{
          position: 'absolute',
          top: { xs: 16, md: 24 },
          right: { xs: 16, md: 28 },
          zIndex: 2,
          color: 'rgba(255,255,255,0.92)',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.85rem',
          fontFamily,
          letterSpacing: '0.02em',
          px: 1.5,
          py: 0.75,
          borderRadius: 2,
          border: '1px solid rgba(255,255,255,0.22)',
          bgcolor: 'rgba(255,255,255,0.06)',
          backdropFilter: 'blur(8px)',
          '&:hover': {
            bgcolor: 'rgba(255,255,255,0.14)',
            borderColor: 'rgba(255,255,255,0.35)',
          },
        }}
      >
        {isArabic ? 'English' : 'العربية'}
      </Button>

      {/* Brand + sign-in composition */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'center', md: 'flex-start' },
          px: { xs: 2.5, sm: 4, md: 8, lg: 12 },
          py: { xs: 6, md: 8 },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 440,
            animation: `${fadeUp} 0.7s ease-out both`,
          }}
        >
          <Box
            component="img"
            src="/cropped-pixelcut-export-removebg-2048x691.png"
            alt={branding.appName}
            sx={{
              height: { xs: 52, sm: 64 },
              width: 'auto',
              maxWidth: '100%',
              objectFit: 'contain',
              mb: 3,
              filter: 'brightness(0) invert(1)',
              animation: `${fadeUp} 0.75s ease-out 0.05s both`,
            }}
          />

          <Typography
            component="h1"
            sx={{
              color: WARM_WHITE,
              fontFamily,
              fontWeight: 700,
              fontSize: { xs: '1.85rem', sm: '2.15rem' },
              lineHeight: 1.2,
              letterSpacing: isArabic ? 0 : '-0.03em',
              mb: 1,
              animation: `${fadeUp} 0.75s ease-out 0.12s both`,
            }}
          >
            {t('auth.loginHeadline')}
          </Typography>

          <Box
            sx={{
              height: 3,
              borderRadius: 1,
              bgcolor: PRIMARY,
              mb: 2,
              animation: `${accentReveal} 0.6s ease-out 0.35s both`,
            }}
          />

          <Typography
            sx={{
              color: 'rgba(250,248,245,0.78)',
              fontFamily,
              fontWeight: 400,
              fontSize: { xs: '0.95rem', sm: '1.02rem' },
              lineHeight: 1.55,
              mb: 3.5,
              maxWidth: 380,
              animation: `${fadeUp} 0.75s ease-out 0.2s both`,
            }}
          >
            {t('auth.loginTagline')}
          </Typography>

          {/* Form surface — interaction container */}
          <Box
            sx={{
              p: { xs: 2.5, sm: 3.25 },
              borderRadius: 3,
              bgcolor: 'rgba(250, 248, 245, 0.94)',
              border: '1px solid rgba(255,255,255,0.55)',
              boxShadow: '0 24px 48px rgba(8, 12, 20, 0.28)',
              backdropFilter: 'blur(16px)',
              animation: `${fadeUp} 0.8s ease-out 0.28s both`,
            }}
          >
            <Typography
              sx={{
                fontFamily,
                fontWeight: 600,
                fontSize: '1.05rem',
                color: NAVY,
                mb: 0.5,
              }}
            >
              {t('auth.signIn')}
            </Typography>
            <Typography
              sx={{
                fontFamily,
                fontSize: '0.875rem',
                color: 'rgba(15,23,36,0.55)',
                mb: 2.25,
              }}
            >
              {t('auth.loginSubtitle')}
            </Typography>

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  fontFamily,
                  '& .MuiAlert-message': { fontFamily },
                }}
              >
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                required
                fullWidth
                id="identifier"
                label={t('auth.email')}
                name="identifier"
                autoComplete="email"
                autoFocus
                value={formData.identifier}
                onChange={handleChange}
                disabled={loading}
                margin="dense"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined sx={{ color: 'rgba(15,23,36,0.4)', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ ...fieldSx, mb: 1.5 }}
              />
              <TextField
                required
                fullWidth
                name="password"
                label={t('auth.password')}
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
                margin="dense"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: 'rgba(15,23,36,0.4)', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        size="small"
                        disabled={loading}
                      >
                        {showPassword ? (
                          <VisibilityOff sx={{ fontSize: 20 }} />
                        ) : (
                          <Visibility sx={{ fontSize: 20 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={fieldSx}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    disabled={loading}
                    size="small"
                    sx={{
                      color: 'rgba(15,23,36,0.35)',
                      '&.Mui-checked': { color: PRIMARY },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontFamily, fontSize: '0.875rem', color: 'rgba(15,23,36,0.7)' }}>
                    {t('auth.rememberMe')}
                  </Typography>
                }
                sx={{ mt: 1.25, mb: 0.5 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mt: 1.5,
                  py: 1.45,
                  borderRadius: 2,
                  fontFamily,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  letterSpacing: '0.01em',
                  textTransform: 'none',
                  bgcolor: PRIMARY,
                  color: '#fff',
                  boxShadow: `0 10px 28px ${PRIMARY}44`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
                  '&:hover': {
                    bgcolor: '#e05a20',
                    boxShadow: `0 14px 32px ${PRIMARY}55`,
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  '&.Mui-disabled': {
                    bgcolor: `${PRIMARY}99`,
                    color: '#fff',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : t('auth.signIn')}
              </Button>
            </Box>
          </Box>

          <Typography
            sx={{
              mt: 2.5,
              textAlign: { xs: 'center', md: 'start' },
              color: 'rgba(250,248,245,0.55)',
              fontFamily,
              fontSize: '0.75rem',
              letterSpacing: '0.04em',
              animation: `${fadeUp} 0.8s ease-out 0.4s both`,
            }}
          >
            {t('auth.secureLogin')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
